import { useEffect, useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'

function OfferForm({ apiService }) {
  const [formData, setFormData] = useState({
    segment: 'DAM',
    quantity: '',
    price: '',
    direction: 'buy'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [serviceMode, setServiceMode] = useState('manual')
  const [autoTradingEnabled, setAutoTradingEnabled] = useState(false)
  const { showNotification } = useNotification()

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }))
  }

  // Check service status
  const checkServiceStatus = async () => {
    if (!apiService || !apiService.isAuthenticated()) return

    try {
      const response = await apiService.request('/api/service/status')
      if (response.success) {
        setServiceMode(response.service.mode)
        setAutoTradingEnabled(response.service.autoTradingEnabled)
      }
    } catch (error) {
      // Fail silently for status checks
      console.warn('Could not fetch service status:', error.message)
    }
  }

  // Check service status on mount and periodically
  useEffect(() => {
    checkServiceStatus()
    const interval = setInterval(checkServiceStatus, 30000) // Check every 30 seconds
    return () => clearInterval(interval)
  }, [apiService])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Check authentication first
    if (!apiService || !apiService.isAuthenticated()) {
      showNotification('Errore: utente non autenticato', 'error')
      return
    }

    // Validate form
    if (!formData.quantity || !formData.price) {
      showNotification('Errore: completare tutti i campi obbligatori', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await apiService.createOffer(formData.segment, formData)
      
      if (response.success) {
        showNotification('Offerta creata con successo', 'success')
        
        // Reset form
        setFormData({
          segment: 'DAM',
          quantity: '',
          price: '',
          direction: 'buy'
        })
      } else {
        showNotification('Errore durante la creazione dell\'offerta', 'error')
      }
    } catch (error) {
      console.error('Offer submission failed:', error)
      showNotification(`Errore: ${error.message}`, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }



  return (
    <div className="MuiPaper-root MuiCard-root" style={{ padding: '24px', margin: '16px 0' }}>
      <div className="MuiCardHeader-root" style={{ paddingBottom: '16px' }}>
        <div className="MuiCardHeader-content">
          <h2 className="MuiTypography-h5" style={{ margin: 0, color: '#1976d2' }}>
            Crea Offerta di Trading
          </h2>
          <p className="MuiTypography-body2" style={{ color: '#666', margin: '8px 0 0 0' }}>
            Crea un'offerta sui mercati energetici
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="MuiCardContent-root">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Segment */}
            <div className="MuiFormControl-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="segment"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Mercato
              </label>
              <select 
                id="segment"
                name="segment"
                className="MuiInputBase-input MuiOutlinedInput-input"
                value={formData.segment}
                onChange={handleChange('segment')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              >
                <option value="DAM">DAM - Day Ahead Market</option>
                <option value="IDM">IDM - Intraday Market</option>
                <option value="mFRR">mFRR - Manual FRR</option>
              </select>
            </div>

            {/* Direction */}
            <div className="MuiFormControl-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="direction"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Direzione
              </label>
              <select 
                id="direction"
                name="direction"
                className="MuiInputBase-input MuiOutlinedInput-input"
                value={formData.direction}
                onChange={handleChange('direction')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              >
                <option value="buy">Acquisto</option>
                <option value="sell">Vendita</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Quantity */}
            <div className="MuiFormControl-root MuiTextField-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="quantity"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Quantità (MWh) *
              </label>
              <input 
                type="number" 
                id="quantity"
                name="quantity"
                className="MuiInputBase-input MuiOutlinedInput-input"
                placeholder="es. 50"
                min="0.1"
                step="0.1"
                required
                value={formData.quantity}
                onChange={handleChange('quantity')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              />
            </div>

            {/* Price */}
            <div className="MuiFormControl-root MuiTextField-root">
              <label 
                className="MuiInputLabel-root"
                htmlFor="price"
                style={{ color: '#1976d2', fontWeight: 500 }}
              >
                Prezzo (EUR/MWh) *
              </label>
              <input 
                type="number" 
                id="price"
                name="price"
                className="MuiInputBase-input MuiOutlinedInput-input"
                placeholder="es. 80.00"
                min="0"
                step="0.01"
                required
                value={formData.price}
                onChange={handleChange('price')}
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  padding: '14px', 
                  width: '100%',
                  marginTop: '8px' 
                }}
              />
            </div>
          </div>

          {/* Service Mode Indicator & AI Controls */}
          <div>
            {/* Service Mode Status */}
            <div 
              style={{ 
                padding: '12px 16px', 
                backgroundColor: serviceMode === 'manual' ? '#fff3e0' : 
                                serviceMode === 'ai-assisted' ? '#e8f5e9' : 
                                '#ffeaea',
                border: `1px solid ${serviceMode === 'manual' ? '#ff9800' : 
                                    serviceMode === 'ai-assisted' ? '#4caf50' : 
                                    '#f44336'}`,
                borderRadius: '8px',
                marginBottom: '16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {serviceMode === 'manual' ? '👤' : 
                     serviceMode === 'ai-assisted' ? '🤖👤' : 
                     '🤖'}
                  </span>
                  <span style={{ fontWeight: 500, fontSize: '0.95rem' }}>
                    Modalità: {serviceMode === 'manual' ? 'Manuale' : 
                              serviceMode === 'ai-assisted' ? 'AI-Assistita' : 
                              'Completamente Automatica'}
                  </span>
                </div>
                
                {autoTradingEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div 
                      style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        backgroundColor: '#4caf50',
                        animation: 'pulse 2s infinite'
                      }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#2e7d32', fontWeight: 600 }}>
                      AUTO-TRADING ATTIVO
                    </span>
                  </div>
                )}
              </div>
              
              {serviceMode === 'full-auto' && !autoTradingEnabled && (
                <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                  💡 AI disponibile ma non in auto-trading. Le offerte richiedono conferma manuale.
                </p>
              )}
            </div>


          </div>



          {/* Submit Button */}
          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button 
              type="submit"
              disabled={isSubmitting}
              className="MuiButton-root MuiButton-contained MuiButton-containedPrimary"
              style={{ 
                backgroundColor: isSubmitting ? '#ccc' : '#1976d2', 
                color: 'white', 
                padding: '12px 24px', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: isSubmitting ? 'not-allowed' : 'pointer', 
                fontSize: '1rem', 
                fontWeight: 500 
              }}
            >
              {isSubmitting ? 'Creazione...' : 'Crea Offerta'}
            </button>
          </div>

        </div>
      </form>
    </div>
  )
}

export default OfferForm
